import React from 'react';
import {
  Box, Group, Loader, Alert, Text, Title, Table, Paper, Grid,
  Badge, ActionIcon, Tooltip, SimpleGrid, Stack, Divider
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconAdjustments, IconBook } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import DraftBadge from '../../common/DraftBadge';
import config from '../../../config';

const COMP_YEAR = 2026;

function fmt$(n, dash = '—') {
  if (n == null) return dash;
  const abs = Math.abs(Math.round(n));
  const str = '$' + abs.toLocaleString();
  return n < 0 ? `(${str})` : str;
}

function fmtPct(n, dash = '—') {
  if (n == null) return dash;
  return (n * 100).toFixed(1) + '%';
}

function fmtMult(n) {
  if (n == null) return '—';
  return n.toFixed(2) + '×';
}

function pctColor(v) {
  if (v == null || v === 0) return 'dimmed';
  if (v < 0.50) return 'red';
  if (v < 0.85) return 'orange';
  if (v >= 1.00) return 'green';
  return 'yellow';
}

function varColor(v, hasData) {
  if (!hasData || v == null) return 'dimmed';
  if (v < -0.05) return 'red';
  if (v >= 0.02) return 'teal';
  return 'dimmed';
}

// Find the highest payout table row that applies to a value (floor lookup)
function activeRow(value, rows) {
  if (value == null) return null;
  return rows
    .filter(r => value >= r.thresholdMin)
    .sort((a, b) => b.thresholdMin - a.thresholdMin)[0] ?? null;
}

const ROW_LABELS = [
  { key: 'salesTarget',    label: 'Target',        fmt: q => fmt$(q.salesTarget) },
  { key: 'revenue',        label: 'Revenue',        fmt: q => fmt$(q.revenue) },
  { key: 'pctOfPlan',      label: '% of Plan',      fmt: q => fmtPct(q.pctOfPlan),        color: q => pctColor(q.pctOfPlan) },
  { key: 'variance',       label: 'SvSP Variance',  fmt: q => q.hasVarianceData ? fmtPct(q.variance) : '—', color: q => varColor(q.variance, q.hasVarianceData) },
  { key: 'varianceRaw',    label: 'SvSP pre-DOS',   fmt: q => q.varianceRaw != null ? fmtPct(q.varianceRaw) : '—', dim: true },
  { key: 'baselineBonus',  label: 'Baseline Bonus', fmt: q => fmt$(q.baselineBonus) },
  { key: 'revenuePayout',  label: 'Rev Payout',     fmt: q => fmtMult(q.revenuePayout) },
  { key: 'adjustedBonus',  label: 'Adj Bonus',      fmt: q => fmt$(q.adjustedBonus) },
  { key: 'variancePayout', label: 'Var Payout',     fmt: q => q.hasVarianceData ? fmtMult(q.variancePayout) : '—' },
  { key: 'finalEarned',    label: 'Earned',         fmt: q => fmt$(q.finalEarned), bold: true },
  { key: 'holdback',       label: 'Holdback (25%)', fmt: q => q.quarter === 0 ? '—' : fmt$(q.holdback) },
  { key: 'paidThisPeriod', label: 'Paid',           fmt: q => fmt$(q.paidThisPeriod, '$0'), bold: true,
    color: q => q.paidThisPeriod < 0 ? 'red' : undefined },
];

export default function RepBonusDashboard() {
  const { repCode } = useParams();
  const navigate = useNavigate();

  const summaryUrl = `${config.apiBaseUrl}/sales/commission/my-summary?repCode=${repCode}&compYear=${COMP_YEAR}`;
  const tablesUrl  = `${config.apiBaseUrl}/sales/commission/payout-tables?compYear=${COMP_YEAR}`;

  const { data: summary, isLoading: summaryLoading, error: summaryError } = useAuthenticatedData(summaryUrl);
  const { data: payoutTables } = useAuthenticatedData(tablesUrl);

  const revTables = (payoutTables ?? []).filter(t => t.tableType === 'Revenue');
  const varTables = (payoutTables ?? []).filter(t => t.tableType === 'Variance');

  const quarters = summary?.quarters ?? [];
  const fy = summary?.fullYear;
  const allPeriods = [...quarters, ...(fy ? [fy] : [])];

  // Summary totals
  const totalPaid    = quarters.reduce((s, q) => s + (q.paidThisPeriod ?? 0), 0);
  const eoyPayout    = fy?.paidThisPeriod ?? 0;
  const finalPayout  = totalPaid + eoyPayout;

  // For payout table highlighting: use FY values if available
  const fyRevPct = fy?.pctOfPlan;
  const fyVar    = fy?.hasVarianceData ? fy?.variance : null;

  if (summaryLoading) return <Box p="xl"><Loader /></Box>;
  if (summaryError) return (
    <Box p="xl">
      <Alert icon={<IconAlertCircle />} color="red">Failed to load commission data.</Alert>
    </Box>
  );
  if (!summary) return <Box p="xl"><Text c="dimmed">No data found for {repCode}.</Text></Box>;

  return (
    <Box p="xl">
      <DraftBadge />
      {/* Header */}
      <Group mb="md">
        <ActionIcon variant="subtle" onClick={() => navigate('/sales/manager/commissions')}>
          <IconArrowLeft size={18} />
        </ActionIcon>
        <Title order={2}>{summary.displayName || repCode}</Title>
        <Badge size="lg" variant="light">{summary.planName}</Badge>
        <Text c="dimmed" size="sm">{COMP_YEAR}</Text>
        <Box style={{ flex: 1 }} />
        <Tooltip label="How commissions work">
          <ActionIcon
            variant="subtle"
            component="a"
            href="/sales/commissions/documentation"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconBook size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Adjust invoices">
          <ActionIcon
            variant="light"
            onClick={() => navigate(`/sales/manager/invoices?ccode=${repCode}&from=${COMP_YEAR}-01-01&to=${COMP_YEAR + 1}-01-01`)}
          >
            <IconAdjustments size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Plan parameters */}
      <SimpleGrid cols={4} mb="lg">
        {[
          { label: 'Annual Target',     value: fmt$(summary.planAnnualTarget) },
          { label: 'Base Bonus',        value: fmt$(summary.planBaseBonus) },
          { label: 'Quarterly Target',  value: fmt$(summary.quarters[0]?.salesTarget) },
          { label: 'Holdback Rate',     value: fmtPct(summary.planHoldbackRate) },
        ].map(({ label, value }) => (
          <Paper key={label} withBorder p="sm" ta="center">
            <Text size="xs" c="dimmed">{label}</Text>
            <Text fw={600} size="lg">{value}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Quarterly breakdown table */}
      <Paper withBorder mb="lg">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ minWidth: 140 }}>Metric</Table.Th>
              {quarters.map(q => <Table.Th key={q.quarter} style={{ textAlign: 'right' }}>{q.periodName}</Table.Th>)}
              {fy && <Table.Th style={{ textAlign: 'right', borderLeft: '2px solid var(--mantine-color-gray-3)' }}>Full Year</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ROW_LABELS.map(({ key, label, fmt, color, bold, dim }) => (
              <Table.Tr key={key}>
                <Table.Td>
                  <Text size={dim ? 'xs' : 'sm'} c="dimmed" fs={dim ? 'italic' : undefined}>{label}</Text>
                </Table.Td>
                {quarters.map(q => {
                  const val = fmt(q);
                  const c = dim ? 'dimmed' : (color ? color(q) : undefined);
                  return (
                    <Table.Td key={q.quarter} style={{ textAlign: 'right' }}>
                      <Text size={dim ? 'xs' : 'sm'} c={c} fw={bold ? 600 : undefined}>{val}</Text>
                    </Table.Td>
                  );
                })}
                {fy && (
                  <Table.Td style={{ textAlign: 'right', borderLeft: '2px solid var(--mantine-color-gray-3)' }}>
                    <Text size={dim ? 'xs' : 'sm'} c={dim ? 'dimmed' : (color ? color(fy) : undefined)} fw={bold ? 600 : undefined}>{fmt(fy)}</Text>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Summary boxes */}
      <SimpleGrid cols={3} mb="xl">
        <Paper withBorder p="md" ta="center">
          <Text size="xs" c="dimmed">Quarterly Bonus Paid</Text>
          <Text fw={700} size="xl">{fmt$(totalPaid, '$0')}</Text>
        </Paper>
        <Paper withBorder p="md" ta="center">
          <Text size="xs" c="dimmed">EOY Payout / Clawback</Text>
          <Text fw={700} size="xl" c={eoyPayout < 0 ? 'red' : undefined}>{fmt$(eoyPayout, '$0')}</Text>
        </Paper>
        <Paper withBorder p="md" ta="center">
          <Text size="xs" c="dimmed">Final Total</Text>
          <Text fw={700} size="xl" c={finalPayout < 0 ? 'red' : undefined}>{fmt$(finalPayout, '$0')}</Text>
        </Paper>
      </SimpleGrid>

      {/* Payout reference tables */}
      {payoutTables && payoutTables.length > 0 && (
        <Grid>
          <Grid.Col span={6}>
            <Paper withBorder p="md">
              <Text fw={600} mb="xs" size="sm">Revenue Payout Schedule</Text>
              <Table size="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>% of Plan</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Multiplier</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Begins at</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {revTables.map(row => {
                    const active = fyRevPct != null && activeRow(fyRevPct, revTables)?.thresholdMin === row.thresholdMin;
                    return (
                      <Table.Tr key={row.thresholdMin} style={active ? { background: 'var(--mantine-color-blue-light)' } : {}}>
                        <Table.Td><Text size="xs" fw={active ? 700 : undefined} c={active ? 'blue' : undefined}>≥ {fmtPct(row.thresholdMin)}</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}><Text size="xs" c={active ? 'blue' : undefined}>{fmtMult(row.payoutRate)}</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="xs" c={active ? 'blue' : 'dimmed'}>
                            {fmt$(summary.quarters[0]?.salesTarget ? summary.quarters[0].salesTarget * 4 * row.thresholdMin : null)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>
          </Grid.Col>

          <Grid.Col span={6}>
            <Paper withBorder p="md">
              <Text fw={600} mb="xs" size="sm">Variance Payout Schedule</Text>
              <Table size="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>SvSP Variance</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Multiplier</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {varTables.map(row => {
                    const active = fyVar != null && activeRow(fyVar, varTables)?.thresholdMin === row.thresholdMin;
                    return (
                      <Table.Tr key={row.thresholdMin} style={active ? { background: 'var(--mantine-color-blue-light)' } : {}}>
                        <Table.Td><Text size="xs" fw={active ? 700 : undefined} c={active ? 'blue' : undefined}>≥ {fmtPct(row.thresholdMin)}</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}><Text size="xs" c={active ? 'blue' : undefined}>{fmtMult(row.payoutRate)}</Text></Table.Td>
                      </Table.Tr>
                    );
                  })}
                  <Table.Tr style={{ background: 'var(--mantine-color-red-0)' }}>
                    <Table.Td><Text size="xs" c="dimmed">&lt; -5%</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}><Text size="xs" c="red">0× (no bonus)</Text></Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Paper>
          </Grid.Col>
        </Grid>
      )}
    </Box>
  );
}
